// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { DateTime } from 'luxon';
import type { PartialDeep } from 'type-fest';

import * as DateHelpers from '../../../utils/dateHelpers';
import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import Enums from '../Enums';
import {
  NewProjectCustomField,
  type ProjectCustomField,
  type ProjectCustomFieldWireFormat,
} from '../ProjectCustomField/ProjectCustomField';
import {
  NewProjectSummaryCompany,
  type ProjectSummaryCompany,
  type ProjectSummaryCompanyWireFormat,
} from '../ProjectSummaryCompany/ProjectSummaryCompany';
import {
  NewProjectSummaryPlant,
  type ProjectSummaryPlant,
  type ProjectSummaryPlantWireFormat,
} from '../ProjectSummaryPlant/ProjectSummaryPlant';

export class ProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly projectJobNumber: string | null;
  readonly projectNumber: string | null;
  readonly owner: string | null;
  readonly contractorDisplay: string;
  readonly statusName: string;
  readonly salesRepName: string;
  readonly plantName: string;
  readonly plants: ProjectSummaryPlant[];
  readonly companies: ProjectSummaryCompany[];
  readonly marketId: string;
  readonly divisionId: string | null;
  readonly statusId: string;
  readonly statusSystemBehavior: Enums.ProjectStatusSystemBehavior;
  readonly isArchived: boolean;
  readonly bidDate: string | null;
  readonly expirationDate: string | null;
  readonly totalCosts: string;
  readonly estimatedMargin: string;
  readonly currencyCode: Enums.CurrencyCode;
  readonly estimatedMarginPercent: number;
  readonly estimatedCuyd: string;
  readonly revenue: string;
  readonly segment: string | null;
  readonly address: string;
  readonly addressLatitude: string | null;
  readonly addressLongitude: string | null;
  readonly customFieldMap: { [key: string]: ProjectCustomField };
  readonly createdBy: string;
  readonly createdAt: string;
  readonly lastStatusChange: DateTime | null;
  readonly lastStatusChangeBy: string | null;
  readonly readyMixQuantity: string;
  readonly readyMixQuantityUnit: Enums.Unit;
  readonly asphaltQuantity: string;
  readonly asphaltQuantityUnit: Enums.Unit;
  readonly aggregateQuantity: string;
  readonly aggregateQuantityUnit: Enums.Unit;
  readonly productCategory: Enums.ProductCategory | null;
  readonly plantIsArchived: boolean;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.name = data.name === undefined ? '' : data.name;
    this.projectJobNumber = data.projectJobNumber === undefined ? null : data.projectJobNumber;
    this.projectNumber = data.projectNumber === undefined ? null : data.projectNumber;
    this.owner = data.owner === undefined ? null : data.owner;
    this.contractorDisplay = data.contractorDisplay === undefined ? '' : data.contractorDisplay;
    this.statusName = data.statusName === undefined ? '' : data.statusName;
    this.salesRepName = data.salesRepName === undefined ? '' : data.salesRepName;
    this.plantName = data.plantName === undefined ? '' : data.plantName;
    this.plants = (data.plants ?? []).map((plants: any) => NewProjectSummaryPlant(plants));
    this.companies = (data.companies ?? []).map((companies: any) =>
      NewProjectSummaryCompany(companies),
    );
    this.marketId = data.marketId === undefined ? NIL_UUID : data.marketId;
    this.divisionId = data.divisionId === undefined ? null : data.divisionId;
    this.statusId = data.statusId === undefined ? NIL_UUID : data.statusId;
    this.statusSystemBehavior =
      data.statusSystemBehavior === undefined
        ? Enums.ProjectStatusSystemBehavior.Custom
        : data.statusSystemBehavior;
    this.isArchived = data.isArchived === undefined ? false : data.isArchived;
    this.bidDate = data.bidDate === undefined ? null : data.bidDate;
    this.expirationDate = data.expirationDate === undefined ? null : data.expirationDate;
    this.totalCosts = data.totalCosts === undefined ? '' : data.totalCosts;
    this.estimatedMargin = data.estimatedMargin === undefined ? '' : data.estimatedMargin;
    this.currencyCode =
      data.currencyCode === undefined ? Enums.CurrencyCode.USD : data.currencyCode;
    this.estimatedMarginPercent =
      data.estimatedMarginPercent === undefined ? 0 : data.estimatedMarginPercent;
    this.estimatedCuyd = data.estimatedCuyd === undefined ? '0' : data.estimatedCuyd;
    this.revenue = data.revenue === undefined ? '' : data.revenue;
    this.segment = data.segment === undefined ? null : data.segment;
    this.address = data.address === undefined ? '' : data.address;
    this.addressLatitude = data.addressLatitude === undefined ? null : data.addressLatitude;
    this.addressLongitude = data.addressLongitude === undefined ? null : data.addressLongitude;
    this.customFieldMap = Object.fromEntries(
      Object.entries(data.customFieldMap ?? {}).map(([key, customFieldMap]: any) => [
        key,
        NewProjectCustomField(customFieldMap),
      ]),
    );
    this.createdBy = data.createdBy === undefined ? NIL_UUID : data.createdBy;
    this.createdAt = data.createdAt === undefined ? '' : data.createdAt;
    this.lastStatusChange = DateHelpers.ParseServerNullableDate(data.lastStatusChange);
    this.lastStatusChangeBy =
      data.lastStatusChangeBy === undefined ? null : data.lastStatusChangeBy;
    this.readyMixQuantity = data.readyMixQuantity === undefined ? '0' : data.readyMixQuantity;
    this.readyMixQuantityUnit =
      data.readyMixQuantityUnit === undefined ? Enums.Unit.CuYd : data.readyMixQuantityUnit;
    this.asphaltQuantity = data.asphaltQuantity === undefined ? '0' : data.asphaltQuantity;
    this.asphaltQuantityUnit =
      data.asphaltQuantityUnit === undefined ? Enums.Unit.Tn : data.asphaltQuantityUnit;
    this.aggregateQuantity = data.aggregateQuantity === undefined ? '0' : data.aggregateQuantity;
    this.aggregateQuantityUnit =
      data.aggregateQuantityUnit === undefined ? Enums.Unit.Tn : data.aggregateQuantityUnit;
    this.productCategory = data.productCategory === undefined ? null : data.productCategory;
    this.plantIsArchived = data.plantIsArchived === undefined ? false : data.plantIsArchived;
  }

  static zero(): ProjectSummary {
    const zeroValues: ExcludeMethodsDeep<ProjectSummary> = {
      id: NIL_UUID,
      name: '',
      projectJobNumber: null,
      projectNumber: null,
      owner: null,
      contractorDisplay: '',
      statusName: '',
      salesRepName: '',
      plantName: '',
      plants: [],
      companies: [],
      marketId: NIL_UUID,
      divisionId: null,
      statusId: NIL_UUID,
      statusSystemBehavior: Enums.ProjectStatusSystemBehavior.Custom,
      isArchived: false,
      bidDate: null,
      expirationDate: null,
      totalCosts: '',
      estimatedMargin: '',
      currencyCode: Enums.CurrencyCode.USD,
      estimatedMarginPercent: 0,
      estimatedCuyd: '0',
      revenue: '',
      segment: null,
      address: '',
      addressLatitude: null,
      addressLongitude: null,
      customFieldMap: {},
      createdBy: NIL_UUID,
      createdAt: '',
      lastStatusChange: null,
      lastStatusChangeBy: null,
      readyMixQuantity: '0',
      readyMixQuantityUnit: Enums.Unit.CuYd,
      asphaltQuantity: '0',
      asphaltQuantityUnit: Enums.Unit.Tn,
      aggregateQuantity: '0',
      aggregateQuantityUnit: Enums.Unit.Tn,
      productCategory: null,
      plantIsArchived: false,
    };
    return new ProjectSummary(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type ProjectSummaryWireFormat = {
  id: string;
  name: string;
  projectJobNumber: string | null;
  projectNumber: string | null;
  owner: string | null;
  contractorDisplay: string;
  statusName: string;
  salesRepName: string;
  plantName: string;
  plants: ProjectSummaryPlantWireFormat[];
  companies: ProjectSummaryCompanyWireFormat[];
  marketId: string;
  divisionId: string | null;
  statusId: string;
  statusSystemBehavior: string;
  isArchived: boolean;
  bidDate: string | null;
  expirationDate: string | null;
  totalCosts: string;
  estimatedMargin: string;
  currencyCode: string;
  estimatedMarginPercent: number;
  estimatedCuyd: string;
  revenue: string;
  segment: string | null;
  address: string;
  addressLatitude: string | null;
  addressLongitude: string | null;
  customFieldMap: { [key: string]: ProjectCustomFieldWireFormat };
  createdBy: string;
  createdAt: string;
  lastStatusChange: string | null;
  lastStatusChangeBy: string | null;
  readyMixQuantity: string;
  readyMixQuantityUnit: string;
  asphaltQuantity: string;
  asphaltQuantityUnit: string;
  aggregateQuantity: string;
  aggregateQuantityUnit: string;
  productCategory: string | null;
  plantIsArchived: boolean;
};

type NewProjectSummaryProps = PartialDeep<ProjectSummary, { recurseIntoArrays: true }>;
export const NewProjectSummary: (props: NewProjectSummaryProps) => ProjectSummary = (props) =>
  new ProjectSummary(props);

type NewProjectSummaryFromWireFormatProps = PartialDeep<
  ProjectSummaryWireFormat,
  { recurseIntoArrays: true }
>;
export const NewProjectSummaryFromWireFormat: (
  props: NewProjectSummaryFromWireFormatProps,
) => ProjectSummary = (props) => new ProjectSummary(props);
